import { z } from 'zod'
import type { ComponentType } from 'react'

// ─── Identifiants ───────────────────────────────────────────────────────────
// docs/4-technique/practice/SPEC.md §4 (types d'items) et §5 (mécaniques).

/** Les huit types d'items du catalogue (SPEC §4). Détermine la forme du `payload`. */
export const ITEM_TYPES = [
  'qcm',
  'vrai_faux',
  'paire',
  'ordre',
  'localisation',
  'numerique',
  'classement',
  'texte_a_trous',
] as const

export type ItemType = (typeof ITEM_TYPES)[number]

/** Les seize mécaniques du catalogue (SPEC §5), identifiées par `game.id`. */
export const GAME_IDS = [
  'sprint',
  'vrai_faux_express',
  'cas_pratique',
  'intrus',
  'appariement',
  'memory_definitions',
  'mot_juste',
  'frise',
  'chaine_causale',
  'hierarchie',
  'carte_muette',
  'schema_etiquete',
  'calcul_applique',
  'estimation',
  'tri_categoriel',
  'texte_a_trous',
] as const

export type GameId = (typeof GAME_IDS)[number]

/** `chrono` : une durée fixe partagée par toute la manche. `manches` : un nombre d'items fixe, sans horloge. */
export type PracticeMode = 'chrono' | 'manches'

/** SPEC §8 — deux compromis retour/anti-triche : `solo` corrige en local, `daily`/`duel` corrigent serveur. */
export type RunMode = 'solo' | 'daily' | 'duel'

export type RunStatus = 'running' | 'finished' | 'abandoned'

// ─── Payloads des huit types d'items (SPEC §4) ─────────────────────────────
//
// Chaque payload est défini en deux temps : un schéma d'objet « nu », gardé
// en interne pour pouvoir dériver la forme *servie* (voir plus bas), puis le
// schéma *canonique* exporté, qui ajoute les règles inter-champs de la SPEC
// (`superRefine`) là où il y en a. Ces règles sont les mêmes que les
// validateurs PHP `Domains/Knowledge/Validators/{Type}Validator.php` devront
// implémenter côté serveur ; les avoir des deux côtés rend une divergence
// future visible plutôt que silencieuse.

const qcmPayloadObjectSchema = z.object({
  scenario: z.string().nullable(),
  question: z.string(),
  options: z.array(z.string()).min(2).max(6),
  correct: z.array(z.number().int().nonnegative()).min(1),
  multiple: z.boolean(),
  explanation: z.string(),
})

/** SPEC §4.1 — taille 1 quand `multiple` vaut `false`, indices tous valides dans `options`. */
export const qcmPayloadSchema = qcmPayloadObjectSchema.superRefine((payload, ctx) => {
  if (!payload.multiple && payload.correct.length !== 1) {
    ctx.addIssue({
      code: 'custom',
      message: 'correct doit contenir exactement un index quand multiple vaut false.',
      path: ['correct'],
    })
  }

  payload.correct.forEach((index, position) => {
    if (index >= payload.options.length) {
      ctx.addIssue({
        code: 'custom',
        message: `Index ${index} hors bornes de options (0..${payload.options.length - 1}).`,
        path: ['correct', position],
      })
    }
  })
})
export type QcmPayload = z.infer<typeof qcmPayloadSchema>

const vraiFauxPayloadObjectSchema = z.object({
  assertion: z.string(),
  answer: z.boolean(),
  explanation: z.string(),
})
export const vraiFauxPayloadSchema = vraiFauxPayloadObjectSchema
export type VraiFauxPayload = z.infer<typeof vraiFauxPayloadSchema>

const pairePayloadObjectSchema = z.object({
  left: z.string(),
  right: z.string(),
  hint: z.string().nullable(),
  /**
   * Candidates affichées pour la carte définition de `memory_definitions`
   * (SPEC §5, lot M2) — la bonne réponse (`right`) plus jusqu'à trois
   * distracteurs tirés d'autres items `paire` de la même manche, mélangés
   * côté serveur. Jamais dans le payload canonique tel qu'écrit par un
   * auteur de contenu (SPEC §4.3 ne le prévoit pas), calculé par le serveur
   * au moment de servir la partie (`Practice\DTOs\RunDto`, même mécanisme
   * que `numerique.choices` d'`estimation`, lot M4). `appariement` et
   * `mot_juste` ne le portent jamais.
   */
  choices: z.array(z.string()).optional(),
})
export const pairePayloadSchema = pairePayloadObjectSchema
export type PairePayload = z.infer<typeof pairePayloadSchema>

export const ordreKindSchema = z.enum(['chronologie', 'causale', 'hierarchie'])
export type OrdreKind = z.infer<typeof ordreKindSchema>

const ordrePayloadObjectSchema = z.object({
  instruction: z.string(),
  kind: ordreKindSchema,
  /** Ordre du tableau = ordre correct (SPEC §4.4). Le mélange se fait à la présentation, depuis la graine. */
  elements: z.array(z.string()).min(3).max(8),
  explanation: z.string(),
})
export const ordrePayloadSchema = ordrePayloadObjectSchema
export type OrdrePayload = z.infer<typeof ordrePayloadSchema>

export const localisationTargetSchema = z.object({
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  /** Rayon de tolérance, dans le même espace normalisé 0..1. */
  r: z.number().min(0).max(1),
})
export type LocalisationTarget = z.infer<typeof localisationTargetSchema>

const localisationPayloadObjectSchema = z.object({
  prompt: z.string(),
  /** Fond de carte : `frontend/public/maps/{map}.svg`, viewBox `0 0 1000 1000`. */
  map: z.string(),
  /** Coordonnées normalisées 0..1, jamais des pixels. */
  target: localisationTargetSchema,
  explanation: z.string(),
})
export const localisationPayloadSchema = localisationPayloadObjectSchema
export type LocalisationPayload = z.infer<typeof localisationPayloadSchema>

export const numeriqueModeSchema = z.enum(['exact', 'ordre_de_grandeur'])
export type NumeriqueMode = z.infer<typeof numeriqueModeSchema>

const numeriquePayloadObjectSchema = z.object({
  question: z.string(),
  answer: z.number(),
  unit: z.string(),
  /** Fraction relative : 0.02 = ±2 %. */
  tolerance: z.number(),
  mode: numeriqueModeSchema,
  explanation: z.string(),
  /**
   * Quatre propositions d'ordre de grandeur pour `estimation` (mode
   * `ordre_de_grandeur` uniquement) — jamais dans le payload canonique tel
   * qu'écrit par un auteur de contenu (SPEC §4.6 ne le prévoit pas), calculé
   * par le serveur au moment de servir la partie (`Practice\DTOs\RunDto`,
   * lot M4) pour que le mode classé conserve de quoi afficher ces quatre
   * choix même une fois `answer` amputé (SPEC §8). `calcul_applique` (mode
   * `exact`) ne le porte jamais.
   */
  choices: z.array(z.number()).length(4).optional(),
})
export const numeriquePayloadSchema = numeriquePayloadObjectSchema
export type NumeriquePayload = z.infer<typeof numeriquePayloadSchema>

export const classementElementSchema = z.object({
  label: z.string(),
  /** Index dans `categories`. */
  category: z.number().int().nonnegative(),
})
export type ClassementElement = z.infer<typeof classementElementSchema>

const classementPayloadObjectSchema = z.object({
  instruction: z.string(),
  categories: z.array(z.string()).min(2).max(4),
  elements: z.array(classementElementSchema).min(4).max(12),
  explanation: z.string(),
})

/** SPEC §4.7 — chaque catégorie représentée par au moins un élément, indices de `category` valides. */
export const classementPayloadSchema = classementPayloadObjectSchema.superRefine((payload, ctx) => {
  const categoryCount = payload.categories.length
  const represented = new Set<number>()

  payload.elements.forEach((element, index) => {
    if (element.category >= categoryCount) {
      ctx.addIssue({
        code: 'custom',
        message: `Catégorie ${element.category} hors bornes de categories (0..${categoryCount - 1}).`,
        path: ['elements', index, 'category'],
      })
      return
    }
    represented.add(element.category)
  })

  if (represented.size < categoryCount) {
    ctx.addIssue({
      code: 'custom',
      message: 'Chaque catégorie doit être représentée par au moins un élément.',
      path: ['categories'],
    })
  }
})
export type ClassementPayload = z.infer<typeof classementPayloadSchema>

const texteATrousPayloadObjectSchema = z.object({
  /** Autant de `{_}` que d'entrées dans `answers`. */
  template: z.string(),
  answers: z.array(z.string()),
  /** Variantes acceptées par trou, même index que `answers`. */
  accept: z.array(z.array(z.string())),
  explanation: z.string(),
})

/** SPEC §4.8 — autant de `{_}` dans `template` que d'entrées dans `answers`, `accept` de même taille. */
export const texteATrousPayloadSchema = texteATrousPayloadObjectSchema.superRefine((payload, ctx) => {
  const blanksCount = (payload.template.match(/\{_\}/g) ?? []).length

  if (blanksCount !== payload.answers.length) {
    ctx.addIssue({
      code: 'custom',
      message: `template contient ${blanksCount} trou(s) pour ${payload.answers.length} entrée(s) dans answers.`,
      path: ['answers'],
    })
  }

  if (payload.accept.length !== payload.answers.length) {
    ctx.addIssue({
      code: 'custom',
      message: 'accept doit avoir autant d’entrées que answers.',
      path: ['accept'],
    })
  }
})
export type TexteATrousPayload = z.infer<typeof texteATrousPayloadSchema>

// ─── Item (enveloppe + union discriminée) ──────────────────────────────────

const itemEnvelopeSchema = z.object({
  id: z.string(),
  topic_id: z.string(),
  difficulty: z.number().int().min(1).max(5),
})

/** Union discriminée sur `type` — un item du catalogue, forme canonique (avec ses réponses). */
export const practiceItemSchema = z.discriminatedUnion('type', [
  itemEnvelopeSchema.extend({ type: z.literal('qcm'), payload: qcmPayloadSchema }),
  itemEnvelopeSchema.extend({ type: z.literal('vrai_faux'), payload: vraiFauxPayloadSchema }),
  itemEnvelopeSchema.extend({ type: z.literal('paire'), payload: pairePayloadSchema }),
  itemEnvelopeSchema.extend({ type: z.literal('ordre'), payload: ordrePayloadSchema }),
  itemEnvelopeSchema.extend({ type: z.literal('localisation'), payload: localisationPayloadSchema }),
  itemEnvelopeSchema.extend({ type: z.literal('numerique'), payload: numeriquePayloadSchema }),
  itemEnvelopeSchema.extend({ type: z.literal('classement'), payload: classementPayloadSchema }),
  itemEnvelopeSchema.extend({ type: z.literal('texte_a_trous'), payload: texteATrousPayloadSchema }),
])

export type PracticeItem = z.infer<typeof practiceItemSchema>

/** Un item du catalogue, restreint au type `T`. Utile pour typer une correction précise. */
export type ItemOfType<T extends ItemType> = Extract<PracticeItem, { type: T }>

// ─── Forme « servie » en mode classé (SPEC §8 : items servis sans réponse) ──
//
// `solo` reçoit la forme canonique ci-dessus, réponses incluses (retour
// immédiat). `daily`/`duel` reçoivent la même forme amputée des champs qui
// révèlent la réponse — la correction n'arrive qu'au `finish()`. Chaque champ
// concerné devient donc *optionnel* plutôt que supprimé du schéma : un
// payload complet (`solo`) et un payload allégé (`daily`/`duel`) sont tous
// deux valides contre ce même schéma servi. Le détail exact de ce que retire
// le serveur par mécanique reste une décision de `Domains/Practice`, hors de
// ce socle — ce schéma se contente de ne pas rejeter ce que la SPEC autorise.
// C'est cette forme que consomment les composants `Round` (`RoundProps.item`) ;
// `ResultScreen` consomme la correction serveur (`ItemCorrection`), jamais elle.

const servedQcmPayloadSchema = qcmPayloadObjectSchema.extend({
  correct: qcmPayloadObjectSchema.shape.correct.optional(),
  explanation: qcmPayloadObjectSchema.shape.explanation.optional(),
})

const servedVraiFauxPayloadSchema = vraiFauxPayloadObjectSchema.extend({
  answer: vraiFauxPayloadObjectSchema.shape.answer.optional(),
  explanation: vraiFauxPayloadObjectSchema.shape.explanation.optional(),
})

/**
 * `paire` (SPEC §4.3) n'a pas une convention unique de « quel champ est la
 * réponse » : elle dépend de la mécanique qui consomme l'item (SPEC §5).
 * `appariement` et `memory_definitions` demandent `right`, `mot_juste`
 * demande `left` (voir `Games/Support/PaireGrading.php` côté serveur). Selon
 * la mécanique, c'est donc l'un ou l'autre qui disparaît en mode classé —
 * contrairement aux sept autres types, les deux champs doivent pouvoir être
 * optionnels ici, jamais un seul figé.
 */
const servedPairePayloadSchema = pairePayloadObjectSchema.extend({
  left: pairePayloadObjectSchema.shape.left.optional(),
  right: pairePayloadObjectSchema.shape.right.optional(),
})

const servedOrdrePayloadSchema = ordrePayloadObjectSchema.extend({
  explanation: ordrePayloadObjectSchema.shape.explanation.optional(),
})

const servedLocalisationPayloadSchema = localisationPayloadObjectSchema.extend({
  target: localisationPayloadObjectSchema.shape.target.optional(),
  explanation: localisationPayloadObjectSchema.shape.explanation.optional(),
})

const servedNumeriquePayloadSchema = numeriquePayloadObjectSchema.extend({
  answer: numeriquePayloadObjectSchema.shape.answer.optional(),
  explanation: numeriquePayloadObjectSchema.shape.explanation.optional(),
})

const servedClassementElementSchema = classementElementSchema.extend({
  category: classementElementSchema.shape.category.optional(),
})
const servedClassementPayloadSchema = classementPayloadObjectSchema.extend({
  elements: z.array(servedClassementElementSchema).min(4).max(12),
  explanation: classementPayloadObjectSchema.shape.explanation.optional(),
})

const servedTexteATrousPayloadSchema = texteATrousPayloadObjectSchema.extend({
  answers: texteATrousPayloadObjectSchema.shape.answers.optional(),
  accept: texteATrousPayloadObjectSchema.shape.accept.optional(),
  explanation: texteATrousPayloadObjectSchema.shape.explanation.optional(),
})

/**
 * Enveloppe d'un item **servi pendant une partie**, distincte de celle d'un item
 * du catalogue.
 *
 * `RunDto::toArray()` ne sert que `{ id, type, payload }` : ni `topic_id`, ni
 * surtout `difficulty`, dont la connaissance aiderait à deviner (SPEC.md §8).
 * Réutiliser `itemEnvelopeSchema`, qui décrit un item du catalogue, faisait
 * échouer la validation au démarrage de toute partie réelle — un désaccord de
 * contrat que seul un parcours de bout en bout non simulé pouvait révéler.
 */
const servedItemEnvelopeSchema = z.object({
  id: z.string(),
})

/** Union discriminée sur `type` — un item tel que servi pendant la partie, réponses ou non. */
export const servedItemSchema = z.discriminatedUnion('type', [
  servedItemEnvelopeSchema.extend({ type: z.literal('qcm'), payload: servedQcmPayloadSchema }),
  servedItemEnvelopeSchema.extend({ type: z.literal('vrai_faux'), payload: servedVraiFauxPayloadSchema }),
  servedItemEnvelopeSchema.extend({ type: z.literal('paire'), payload: servedPairePayloadSchema }),
  servedItemEnvelopeSchema.extend({ type: z.literal('ordre'), payload: servedOrdrePayloadSchema }),
  servedItemEnvelopeSchema.extend({ type: z.literal('localisation'), payload: servedLocalisationPayloadSchema }),
  servedItemEnvelopeSchema.extend({ type: z.literal('numerique'), payload: servedNumeriquePayloadSchema }),
  servedItemEnvelopeSchema.extend({ type: z.literal('classement'), payload: servedClassementPayloadSchema }),
  servedItemEnvelopeSchema.extend({ type: z.literal('texte_a_trous'), payload: servedTexteATrousPayloadSchema }),
])

export type ServedItem = z.infer<typeof servedItemSchema>

/** Un item servi, restreint au type `T`. C'est le type de `RoundProps.item`. */
export type ServedItemOfType<T extends ItemType> = Extract<ServedItem, { type: T }>

// ─── Réponse du joueur ──────────────────────────────────────────────────────

/**
 * Forme de la valeur soumise pour un item de type `T`. Convention du socle —
 * la SPEC ne fige pas cette forme côté client (`grade()` prend `mixed` en PHP) —
 * pensée pour que chaque mécanique future type précisément son `onAnswer` sans
 * avoir à la redéfinir.
 */
export type AnswerValueFor<T extends ItemType> = T extends 'qcm'
  ? number[]
  : T extends 'vrai_faux'
    ? boolean
    : T extends 'paire'
      ? Record<string, string>
      : T extends 'ordre'
        ? string[]
        : T extends 'localisation'
          ? { x: number; y: number }
          : T extends 'numerique'
            ? number
            : T extends 'classement'
              ? Record<string, number>
              : T extends 'texte_a_trous'
                ? string[]
                : never

/** SPEC §12 — corps de `POST /practice/runs/{run}/answers`, un élément du tableau `answers`. */
export const answerSchema = z.object({
  item_id: z.string(),
  value: z.unknown(),
  /**
   * ENTIER, et pas seulement positif : `SubmitAnswersRequest` valide
   * `answers.*.ms` en `integer`. C'est la règle dont la violation a rendu les
   * seize mécaniques injouables — `performance.now()` rend un flottant, et
   * toute soumission partait en 422. Que le schéma l'énonce ici rend une
   * divergence future visible, au lieu de la laisser au seul `Math.round()`
   * de `useItemLatency`.
   */
  ms: z.number().int().nonnegative(),
})
export type Answer = z.infer<typeof answerSchema>

// ─── Partie (practice_runs, SPEC §6.6 et §12) ──────────────────────────────

export const practiceRunSchema = z.object({
  id: z.string(),
  game: z.enum(GAME_IDS),
  mode: z.enum(['solo', 'daily', 'duel']),
  seed: z.number(),
  /**
   * Nul dès que la partie ne restreint aucun topic : `StartRunHandler` sert
   * `null` en mode `daily`, et en `solo`/`duel` quand aucun topic n'a été
   * demandé (la colonne est nullable, migration 620001).
   */
  topic_ids: z.array(z.string()).nullable(),
  status: z.enum(['running', 'finished', 'abandoned']),
  /** Figés au démarrage — jamais recalculés en cours de partie. Forme servie : voir `servedItemSchema`. */
  items: z.array(servedItemSchema),
  score: z.number().nullable(),
  max_score: z.number().nullable(),
  accuracy: z.number().nullable(),
  duration_ms: z.number().nullable(),
  started_at: z.string(),
  ended_at: z.string().nullable(),
})
export type PracticeRun = z.infer<typeof practiceRunSchema>

/** SPEC §12 — corps de `POST /practice/runs`. */
export const startRunPayloadSchema = z.object({
  game: z.enum(GAME_IDS),
  mode: z.enum(['solo', 'daily', 'duel']),
  topic_ids: z.array(z.string()).optional(),
})
export type StartRunPayload = z.infer<typeof startRunPayloadSchema>

/** Correction d'un item, telle que renvoyée par `POST /practice/runs/{run}/finish`. */
export const itemCorrectionSchema = z.object({
  item_id: z.string(),
  is_correct: z.boolean(),
  correct_value: z.unknown(),
  explanation: z.string(),
})
export type ItemCorrection = z.infer<typeof itemCorrectionSchema>

/** SPEC §12 — réponse de `POST /practice/runs/{run}/finish` : score, corrections, XP crédités. */
export const finishRunResultSchema = z.object({
  score: z.number(),
  max_score: z.number(),
  accuracy: z.number(),
  corrections: z.array(itemCorrectionSchema),
  xp_earned: z.number(),
})
export type FinishRunResult = z.infer<typeof finishRunResultSchema>

/**
 * SPEC §12 — une entrée de `GET /practice/games` : mécaniques jouables selon
 * les items disponibles. Forme alignée sur `GetPlayableGamesQuery::execute()`
 * (`Domains/Practice`, côté serveur) : la liste elle-même fait déjà le tri
 * entre jouable ou non (items disponibles ET mécanique implémentée) — ce
 * schéma ne porte donc plus `items_available`, jamais recomposé côté client
 * (SPEC §12, Lot J). `mode`/`duration_ms`/`round_size` viennent de
 * `GameCatalog` (serveur) et suffisent à afficher chaque carte sans deviner.
 */
export const playableGameSchema = z.object({
  id: z.enum(GAME_IDS),
  mode: z.enum(['chrono', 'manches']),
  duration_ms: z.number().int().positive().nullable(),
  round_size: z.number().int().positive(),
})
export type PlayableGame = z.infer<typeof playableGameSchema>

/**
 * Les quatre états de révision d'un item (SM-2, SPEC §10) — alignés sur
 * `ItemReview::STATES` (`Domains/Practice`, côté serveur). `new` ne devrait
 * jamais apparaître dans une réponse de `GET /practice/due` (`ItemReview` n'a
 * de ligne qu'après un premier passage par `ReviewScheduler::schedule()`,
 * voir son docblock) : gardé dans le schéma par prudence plutôt que rejeté,
 * pour ne jamais faire échouer `assertShape` sur un état par ailleurs valide.
 */
export const REVIEW_STATES = ['new', 'learning', 'review', 'relearning'] as const
export type ReviewState = (typeof REVIEW_STATES)[number]

/**
 * Une ligne de `GET /practice/due` (`DueItemsDto`, SPEC §10, §12) — l'état de
 * révision d'un item, JAMAIS son `payload` : une liste de révisions qui
 * porterait les questions et leurs réponses serait une fuite (voir le
 * docblock serveur de `DueItemsDto`). Distinct de `PracticeItem` : ce n'est
 * pas un item du catalogue, `Domains/Practice` ne connaît d'ailleurs pas son
 * contenu — seulement son identifiant et son état SM-2.
 */
export const dueItemSchema = z.object({
  item_id: z.string(),
  state: z.enum(REVIEW_STATES),
  reps: z.number().int().nonnegative(),
  lapses: z.number().int().nonnegative(),
  stability: z.number(),
  difficulty: z.number(),
  due_at: z.string(),
  last_reviewed_at: z.string().nullable(),
})
export type DueItem = z.infer<typeof dueItemSchema>

/**
 * SPEC §12 — réponse de `GET /practice/due` : `DueItemsDto::toArray()`.
 *
 * `count` contre `total` (lot S, correction) : `count` est la taille de la
 * page rendue (bornée par `limit`), `total` le nombre réel d'items dus avant
 * troncature. Les deux divergent dès qu'un apprenant a plus d'items dus que
 * la page n'en montre — exactement le cas que la répétition espacée existe
 * pour traiter (retour après une pause). `DueBanner` doit afficher `total`,
 * jamais `count`.
 */
export const dueItemsSchema = z.object({
  items: z.array(dueItemSchema),
  count: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
})
export type DueItems = z.infer<typeof dueItemsSchema>

/** Motifs de signalement d'un item (SPEC §11.2 et §6.5). */
export const ITEM_REPORT_REASONS = ['wrong_answer', 'unclear', 'outdated', 'inappropriate', 'other'] as const
export type ItemReportReason = (typeof ITEM_REPORT_REASONS)[number]

/**
 * Signature du gestionnaire de signalement (SPEC §11.2), partagée par
 * `ReportItemButton`, `ResultScreen` et `GameShell`. Un type nommé, plutôt
 * qu'une fonction fléchée renvoyant `Promise<...>` écrite en ligne dans un
 * composant : le garde-fou i18n (`translations.test.ts`) scanne tout texte
 * entre `>` et `<`, et confondrait `=> Promise<` avec du texte visible.
 */
export type ReportHandler = (itemId: string, reason: ItemReportReason) => Promise<void> | void

/**
 * Une ligne de `GET /practice/daily/leaderboard` — alignée sur
 * `DailyLeaderboardDto` (`Domains/Practice`) : `avatar_url` et `accuracy`
 * manquaient à ce schéma, écrit avant que ce lot n'existe côté serveur.
 */
export const dailyLeaderboardEntrySchema = z.object({
  rank: z.number().int().positive(),
  user_id: z.string(),
  username: z.string().nullable(),
  avatar_url: z.string().nullable(),
  score: z.number(),
  accuracy: z.number(),
  is_me: z.boolean(),
})
export type DailyLeaderboardEntry = z.infer<typeof dailyLeaderboardEntrySchema>

/** SPEC §12 — réponse de `GET /practice/daily/leaderboard` : `DailyLeaderboardDto::toArray()`. */
export const dailyLeaderboardSchema = z.object({
  /** Date calendaire Paris du classement (`Y-m-d`). */
  date: z.string(),
  entries: z.array(dailyLeaderboardEntrySchema),
})
export type DailyLeaderboard = z.infer<typeof dailyLeaderboardSchema>

/**
 * Résultat déjà obtenu au jeu du jour (`DailyGameDto.$result`, non nul quand
 * `already_played` vaut vrai). `pattern` reste `null` tant que
 * `GetDailyGameQuery::recordResult()` n'a pas été appelée pour cette partie
 * (dégradation documentée côté serveur) — jamais un signe d'échec côté client.
 */
export const dailyGameResultSchema = z.object({
  score: z.number(),
  max_score: z.number(),
  accuracy: z.number(),
  pattern: z.string().nullable(),
  share: z.string(),
  finished_at: z.string(),
})
export type DailyGameResult = z.infer<typeof dailyGameResultSchema>

/**
 * SPEC §12, §9 — réponse de `GET /practice/daily` : `DailyGameDto::toArray()`.
 * Deux états mutuellement exclusifs, distingués par `already_played` (voir le
 * docblock de `DailyGameDto`) : à jouer (`result` nul), ou déjà joué
 * (`result` porte le score déjà obtenu).
 */
export const dailyGameSchema = z.object({
  date: z.string(),
  seed: z.number(),
  game: z.enum(GAME_IDS),
  discipline: z.string(),
  topic_id: z.string(),
  already_played: z.boolean(),
  result: dailyGameResultSchema.nullable(),
})
export type DailyGame = z.infer<typeof dailyGameSchema>

// ─── Contrat de mécanique côté TypeScript (SPEC §7.2 — l'affichage seulement) ─

export interface RoundProps<T extends ItemType = ItemType> {
  /**
   * Item courant, sous sa forme *servie* (`ServedItem`, sans réponse en mode
   * `daily`/`duel`) — jamais la forme canonique. Sa forme dépend du ou des
   * types consommés par la mécanique.
   */
  item: ServedItemOfType<T>
  /** Mode de la partie en cours : conditionne le retour immédiat ou différé (SPEC §8). */
  runMode: RunMode
  /**
   * Appelé quand le joueur valide sa réponse. `ms` est la latence à mesurer
   * par le `Round` lui-même, depuis l'affichage de l'item — via le hook du
   * socle `useItemLatency` (`hooks/useItemLatency.ts`), qui s'appuie sur
   * `performance.now()` pour rester juste. `isCorrect` n'est connu que si la
   * mécanique peut l'évaluer localement (mode `solo`, réponse dans le
   * payload) ; `undefined` sinon, la correction viendra du serveur au
   * `finish()`. En mode `solo`, c'est aussi au `Round` de retarder son appel
   * à `onAnswer` le temps d'afficher son propre retour immédiat (SPEC §8) —
   * `GameShell` avance à la manche suivante dès que `onAnswer` est appelé.
   */
  onAnswer: (value: AnswerValueFor<T>, ms: number, isCorrect?: boolean) => void
  /** Vrai pendant une interruption (ex. confirmation d'abandon) : la manche ignore les entrées. */
  disabled?: boolean
}

/** Registre d'une mécanique — SPEC §7.2. Aucune logique de correction ici, elle est en PHP. */
export interface GameTemplate<T extends ItemType = ItemType> {
  id: GameId
  itemTypes: T[]
  mode: PracticeMode
  /** Durée totale en ms, uniquement pour `mode: 'chrono'`. */
  durationMs?: number
  Round: ComponentType<RoundProps<T>>
}

// ─── Taxonomie et centres d'intérêt (`Domains/Knowledge`, SPEC §6.1, §6.2) ──
//
// Ces routes vivent dans `routes/knowledge.php`, pas `routes/practice.php` —
// mais `InterestPicker` (le composant qui les consomme) habite `practice/`,
// au même titre que `ReportItemButton` plus haut dans ce fichier : l'appel
// suit le composant, pas le découpage des routes serveur.

/** Un nœud de l'arbre des topics (`TopicDto`), avec ses enfants déjà résolus. */
export interface TopicNode {
  id: string
  parent_id: string | null
  slug: string
  discipline: string
  /** 0 discipline, 1 thème, 2 sous-thème (SPEC §6.1). */
  level: number
  position: number
  /** `{"fr": "...", "en": "..."}` — repli sur `fr` à l'affichage (SPEC §3). */
  labels: Record<string, string>
  is_active: boolean
  items_count: number
  children: TopicNode[]
}

export const topicNodeSchema: z.ZodType<TopicNode> = z.lazy(() =>
  z.object({
    id: z.string(),
    parent_id: z.string().nullable(),
    slug: z.string(),
    discipline: z.string(),
    level: z.number().int().min(0).max(2),
    position: z.number().int(),
    labels: z.record(z.string(), z.string()),
    is_active: z.boolean(),
    items_count: z.number().int().nonnegative(),
    children: z.array(topicNodeSchema),
  }),
)

/** Un centre d'intérêt de l'utilisateur (`UserInterestDto`, SPEC §6.2). */
export const userInterestSchema = z.object({
  topic_id: z.string(),
  slug: z.string(),
  discipline: z.string(),
  level: z.number().int().min(0).max(2),
  labels: z.record(z.string(), z.string()),
  weight: z.number().int().min(1).max(5),
  source: z.enum(['explicit', 'derived']),
})
export type UserInterest = z.infer<typeof userInterestSchema>
